<script lang="ts" setup>
  import { computed, inject } from 'vue';

  import { t } from '../../locale';
  import Chip from '../Chip/Chip.vue';
  import { DATA_VIEW_FILTERS_UTILS_INJECTION } from '../DataViewFilters/DataViewFilters.keys';
  import Icon from '../Icon/Icon.vue';

  export interface FilterDrawerItemProps {
    /** The name of a filter group */
    group: string;
    title: string;
    description?: string;
  }
  const props = defineProps<FilterDrawerItemProps>();

  export interface FilterDrawerItemEmits {
    (e: 'navigate');
  }
  const emit = defineEmits<FilterDrawerItemEmits>();

  export interface FilterDrawerItemSlots {
    default?: () => unknown;
  }

  const slots = defineSlots<FilterDrawerItemSlots>();

  const dataViewFiltersUtils = inject(DATA_VIEW_FILTERS_UTILS_INJECTION.key);

  if (!dataViewFiltersUtils?.useFiltersInstance) {
    throw new Error(
      'FilterDropdown must be used within a <DataViewFilters> that receives an instance of useFilters().',
    );
  }

  const { activeFiltersCounts } = dataViewFiltersUtils.useFiltersInstance;

  const activeCount = computed(() => activeFiltersCounts.value[props.group]);
  const isDrawerCascade = computed(() => dataViewFiltersUtils.drawerStyle === 'cascade');
</script>

<template>
  <div class="stash-filter-drawer-item">
    <component
      :is="isDrawerCascade ? 'div' : 'button'"
      data-test="stash-filter-drawer-item|dynamic-component"
      class="outline-hidden w-full border-b border-ice-500 py-4 focus:outline-blue-500"
      @click="isDrawerCascade ? undefined : emit('navigate')"
    >
      <div class="flex items-center justify-between self-stretch">
        <div class="flex flex-col items-start">
          <h4>{{ props.title }}</h4>
          <div v-if="props.description" class="text-xs" data-test="description">
            {{ props.description }}
          </div>
        </div>
        <div class="inline-flex items-center gap-6" :class="{ 'mb-0.5 mr-2': isDrawerCascade }">
          <Chip
            v-if="activeCount"
            color="blue"
            radius="pill"
            shade="main"
            :aria-label="t('ll.numberOfActiveFilters')"
            :title="t('ll.numberOfActiveFilters')"
          >
            {{ activeCount }}
          </Chip>
          <Icon v-if="!isDrawerCascade" name="chevron-right" :title="t('ll.viewFilterGroup')" />
        </div>
      </div>
    </component>
    <div v-if="isDrawerCascade && slots.default" class="gap-3 p-6">
      <slot></slot>
    </div>
  </div>
</template>
